// Engineering Playground - Phase 41.
// Pick a scene (bathroom / kitchen / garden / office), pick Empty or Fully
// Loaded, then drag equipment from a left palette onto the canvas and click
// installed items to operate them.  Photo-real backdrops + equipment PNGs
// come from Gemini via the backend `/api/playground/*` routes.

import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Home, Loader2, Play, Wrench, Sparkles, RefreshCw, Trash2, Info,
} from 'lucide-react';
import { Button } from '../../components/ui/button';

const API = process.env.REACT_APP_BACKEND_URL;

// --- Visual state renderers for each `effect` on a hotspot ---
function EffectOverlay({ effect, state }) {
  if (!state) return null;
  const on = state === 'on' || state === 'active';
  if (effect === 'water_stream') {
    return (
      <div className="absolute inset-x-[38%] top-full h-40 pointer-events-none">
        <div className="w-3 h-full bg-gradient-to-b from-cyan-200/90 via-cyan-300/70 to-cyan-500/40 blur-[1px] mx-auto animate-[waterfall_0.5s_linear_infinite] rounded-full" />
      </div>
    );
  }
  if (effect === 'flush_swirl') {
    return (
      <div className="absolute inset-0 pointer-events-none flex items-center justify-center">
        <div className="w-3/4 h-3/4 rounded-full border-4 border-cyan-300/60 border-t-transparent animate-spin" />
        <div className="absolute w-1/2 h-1/2 rounded-full border-2 border-cyan-200/70 border-b-transparent animate-[spin_0.6s_linear_infinite_reverse]" />
      </div>
    );
  }
  if (effect === 'glow') {
    return (
      <div className="absolute inset-0 pointer-events-none rounded-lg shadow-[0_0_60px_20px_rgba(253,224,71,0.55)] bg-yellow-200/10" />
    );
  }
  if (effect === 'light_toggle') {
    return <div className="absolute inset-0 pointer-events-none rounded-lg bg-amber-200/10 shadow-[inset_0_0_30px_6px_rgba(253,224,71,0.35)]" />;
  }
  if (effect === 'spin') {
    return (
      <div className="absolute inset-0 pointer-events-none flex items-center justify-center">
        <div className="w-3/4 h-3/4 rounded-full border-2 border-white/60 border-dashed animate-spin" />
      </div>
    );
  }
  if (effect === 'flame') {
    return (
      <div className="absolute inset-0 pointer-events-none flex items-end justify-center pb-1">
        <div className="w-3 h-6 bg-gradient-to-t from-orange-500 via-yellow-400 to-transparent rounded-full blur-[1px] animate-[waterfall_0.4s_linear_infinite]" />
        <div className="w-2 h-4 -ml-1 bg-gradient-to-t from-red-500 via-orange-400 to-transparent rounded-full blur-[1px] animate-[waterfall_0.35s_linear_infinite]" />
      </div>
    );
  }
  if (effect === 'spray') {
    return (
      <div className="absolute inset-0 pointer-events-none">
        {[0, 30, 60, 90, 120, 150].map(deg => (
          <div key={deg} className="absolute left-1/2 top-1/2 w-20 h-0.5 bg-gradient-to-r from-cyan-300/80 to-transparent origin-left" style={{ transform: `translate(-50%, -50%) rotate(${deg}deg)` }} />
        ))}
      </div>
    );
  }
  if (effect === 'pump') {
    return <div className="absolute inset-0 pointer-events-none animate-pulse bg-white/10 rounded-lg" />;
  }
  return null;
}

// Cycle state for an effect (2-state toggle for MVP).
function nextState(cur) {
  return cur === 'on' ? 'off' : 'on';
}

// -----------------------------------------------------------------------------
// Main page
// -----------------------------------------------------------------------------
export default function EngineeringPlaygroundPage() {
  const nav = useNavigate();
  const [scenes, setScenes] = useState([]);
  const [sceneId, setSceneId] = useState(null);
  const [manifest, setManifest] = useState(null);
  const [mode, setMode] = useState('loaded'); // 'loaded' | 'empty'
  const [busy, setBusy] = useState(false);
  const [placed, setPlaced] = useState([]); // items placed in Empty mode
  const [activeStates, setActiveStates] = useState({}); // action_uid -> 'on'|'off'
  const [carrying, setCarrying] = useState(null); // {type:'eq'|'tool', id, img, name}
  const canvasRef = useRef(null);

  // Fetch scene list once.
  useEffect(() => {
    axios.get(`${API}/api/playground/scenes`).then(r => {
      setScenes(r.data.scenes || []);
      const first = r.data.scenes?.find(s => s.any_ready) || r.data.scenes?.[0];
      if (first) setSceneId(first.id);
    }).catch(() => toast.error('Could not load scenes'));
  }, []);

  // Fetch manifest when scene changes.
  const loadManifest = useCallback(async () => {
    if (!sceneId) return;
    try {
      const r = await axios.get(`${API}/api/playground/${sceneId}/manifest`);
      setManifest(r.data);
      setActiveStates({});
      setPlaced([]);
    } catch (e) { toast.error('Could not load scene'); }
  }, [sceneId]);
  useEffect(() => { loadManifest(); }, [loadManifest]);

  const generateAll = async () => {
    if (!sceneId) return;
    setBusy(true);
    try {
      toast.info('Rendering the whole scene - this takes 1-2 min the first time.');
      await axios.post(`${API}/api/playground/${sceneId}/generate`, {}, { timeout: 300000 });
      await loadManifest();
      toast.success('Scene ready!');
    } catch (e) { toast.error('Generation failed - check backend logs.'); }
    finally { setBusy(false); }
  };

  const regenSingle = async (kind, id) => {
    if (!sceneId) return;
    try {
      toast.info('Re-rendering...');
      await axios.post(`${API}/api/playground/${sceneId}/generate/${kind}/${id}`, {}, { timeout: 120000 });
      await loadManifest();
    } catch (e) { toast.error('Regen failed'); }
  };

  const bgReady = mode === 'loaded' ? manifest?.loaded_bg?.ready : manifest?.empty_bg?.ready;
  const bgImg   = mode === 'loaded' ? manifest?.loaded_bg?.img   : manifest?.empty_bg?.img;

  const onHotspotClick = (eq, action) => {
    if (mode !== 'loaded') return;
    const uid = `${eq.id}:${action.id}`;
    setActiveStates(s => ({ ...s, [uid]: nextState(s[uid]) }));
    toast(`${action.label}`, { icon: <Sparkles className="w-4 h-4 text-amber-400" /> });
  };

  const onCanvasClick = (e) => {
    if (mode !== 'empty' || !carrying) return;
    const rect = canvasRef.current.getBoundingClientRect();
    const xPct = ((e.clientX - rect.left) / rect.width)  * 100;
    const yPct = ((e.clientY - rect.top)  / rect.height) * 100;
    setPlaced(p => [...p, { ...carrying, uid: `${carrying.type}-${carrying.id}-${Date.now()}`, x: xPct, y: yPct }]);
    setCarrying(null);
  };

  const removePlaced = (uid) => setPlaced(p => p.filter(x => x.uid !== uid));
  const clearCanvas  = () => setPlaced([]);

  const eqAssets  = manifest?.equipment || [];
  const toolAssets = manifest?.tools    || [];
  const anyEqReady = eqAssets.some(e => e.ready);
  const sceneObj = scenes.find(s => s.id === sceneId);

  const roomLightsOff = useMemo(() => {
    return Object.entries(activeStates).some(
      ([uid, st]) => uid.endsWith(':light') && st === 'on'
    );
  }, [activeStates]);

  return (
    <div className="min-h-screen bg-slate-950 text-white" data-testid="engineering-playground">
      <style>{`@keyframes waterfall { from { transform: translateY(-100%); } to { transform: translateY(100%); } }`}</style>

      {/* Header */}
      <div className="max-w-[1400px] mx-auto p-4 sm:p-6">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
          <div>
            <button onClick={() => nav('/labs/engineering-designer')} className="text-xs text-slate-400 hover:text-white flex items-center gap-1" data-testid="back-to-lab">
              <Home className="w-3.5 h-3.5" /> Young Engineer Lab
            </button>
            <h1 className="text-2xl sm:text-3xl font-black mt-1" data-testid="playground-title">
              Engineering Playground - Photo-real Scenes
            </h1>
            <p className="text-sm text-slate-400 mt-1 max-w-2xl">
              Pick a real-world scene. Load it fully-installed, then click on the tap, flush, switch or fan to operate them - or start empty and drop things in from the left.
            </p>
          </div>
          {sceneId && (
            <div className="flex items-center gap-2">
              {!bgReady && (
                <Button size="sm" onClick={generateAll} disabled={busy} className="bg-amber-500 hover:bg-amber-400 text-slate-900 font-bold" data-testid="generate-btn">
                  {busy ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Play className="w-4 h-4 mr-1" />}
                  {busy ? 'Rendering…' : 'Render this scene'}
                </Button>
              )}
              {bgReady && (
                <Button size="sm" variant="outline" onClick={generateAll} disabled={busy} className="border-white/20 text-white hover:bg-white/10" data-testid="regen-btn">
                  {busy ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <RefreshCw className="w-4 h-4 mr-1" />}
                  Regenerate
                </Button>
              )}
            </div>
          )}
        </div>

        {/* Scene picker */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-4" data-testid="scene-picker">
          {scenes.map(s => (
            <button
              key={s.id}
              onClick={() => setSceneId(s.id)}
              className={`text-left rounded-xl px-3 py-2 border-2 transition-all ${sceneId === s.id ? 'bg-amber-500/20 border-amber-400 shadow-lg' : 'bg-white/5 border-white/10 hover:border-white/30'}`}
              data-testid={`scene-${s.id}`}
            >
              <div className="text-sm font-black">{s.title}</div>
              <div className="text-[10px] text-slate-400 flex items-center gap-2 mt-0.5">
                <span>{s.equipment_count} items</span>
                <span>· {s.tool_count} tools</span>
                {s.loaded_ready ? <span className="text-emerald-300">· ready</span> : <span className="text-amber-300">· not rendered</span>}
              </div>
            </button>
          ))}
        </div>

        {/* Mode tabs */}
        {manifest && (
          <div className="inline-flex bg-white/5 rounded-lg p-1 mb-4" data-testid="mode-tabs">
            <button
              onClick={() => setMode('loaded')}
              className={`px-4 py-1.5 text-xs font-bold rounded-md ${mode === 'loaded' ? 'bg-amber-400 text-slate-900' : 'text-slate-300 hover:text-white'}`}
              data-testid="tab-loaded"
            >Fully Loaded</button>
            <button
              onClick={() => setMode('empty')}
              className={`px-4 py-1.5 text-xs font-bold rounded-md ${mode === 'empty' ? 'bg-amber-400 text-slate-900' : 'text-slate-300 hover:text-white'}`}
              data-testid="tab-empty"
            >Empty (build it)</button>
          </div>
        )}

        {/* Main split: palette + canvas */}
        <div className="grid grid-cols-12 gap-4">
          {/* Left palette */}
          <aside className="col-span-12 lg:col-span-3 space-y-4">
            <PalettePanel
              title="Equipment"
              icon={<Sparkles className="w-4 h-4 text-amber-400" />}
              hint={mode === 'empty' ? 'Click an item, then click the canvas to place it.' : 'Fully-loaded mode - click things IN the scene to operate them.'}
              items={eqAssets}
              kind="eq"
              carrying={carrying}
              setCarrying={mode === 'empty' ? setCarrying : null}
              regenSingle={regenSingle}
            />
            <PalettePanel
              title="Tools"
              icon={<Wrench className="w-4 h-4 text-sky-400" />}
              hint="Cartoon tools you can drop in for the vibe."
              items={toolAssets}
              kind="tool"
              carrying={carrying}
              setCarrying={mode === 'empty' ? setCarrying : null}
              regenSingle={regenSingle}
            />
          </aside>

          {/* Canvas */}
          <main className="col-span-12 lg:col-span-9">
            <div
              ref={canvasRef}
              onClick={onCanvasClick}
              className={`relative aspect-video rounded-2xl overflow-hidden border-2 border-white/10 shadow-2xl bg-slate-900 ${carrying && mode === 'empty' ? 'cursor-crosshair' : ''}`}
              data-testid="scene-canvas"
            >
              <AnimatePresence mode="wait">
                {bgReady ? (
                  <motion.img
                    key={`${sceneId}-${mode}`}
                    src={`${API}${bgImg}`}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.35 }}
                    className="absolute inset-0 w-full h-full object-cover"
                    style={{ filter: roomLightsOff ? 'brightness(1.15) saturate(1.1)' : 'brightness(0.85)' }}
                    data-testid="scene-bg"
                    alt=""
                  />
                ) : (
                  <div className="absolute inset-0 flex flex-col items-center justify-center text-slate-400 text-sm gap-2">
                    <Loader2 className={`w-8 h-8 ${busy ? 'animate-spin' : ''} text-amber-400`} />
                    <div>This scene is not rendered yet.</div>
                    <div className="text-xs">Click "Render this scene" above (takes ~1 min the first time).</div>
                  </div>
                )}
              </AnimatePresence>

              {/* Hotspots on Fully Loaded */}
              {bgReady && mode === 'loaded' && eqAssets.map(eq => (
                (eq.actions || []).map(action => {
                  const uid = `${eq.id}:${action.id}`;
                  const st  = activeStates[uid] || 'off';
                  const { x, y, w, h } = eq.hotspot || { x: 50, y: 50, w: 10, h: 10 };
                  return (
                    <div
                      key={uid}
                      onClick={(e) => { e.stopPropagation(); onHotspotClick(eq, action); }}
                      className="absolute cursor-pointer group"
                      style={{ left: `${x}%`, top: `${y}%`, width: `${w}%`, height: `${h}%` }}
                      title={action.label}
                      data-testid={`hotspot-${uid}`}
                    >
                      {/* Ring on hover only */}
                      <div className={`absolute inset-0 rounded-lg border-2 transition-all ${st === 'on' ? 'border-amber-300/80 shadow-[0_0_25px_5px_rgba(253,224,71,0.4)]' : 'border-transparent group-hover:border-amber-400/70 group-hover:shadow-[0_0_20px_2px_rgba(253,224,71,0.35)]'}`} />
                      <div className="absolute inset-x-0 -top-6 opacity-0 group-hover:opacity-100 transition-opacity text-[10px] font-bold text-white bg-black/70 px-1.5 py-0.5 rounded text-center whitespace-nowrap">
                        {action.label}
                      </div>
                      <EffectOverlay effect={action.effect} state={st} />
                    </div>
                  );
                })
              ))}

              {/* Placed items on Empty */}
              {bgReady && mode === 'empty' && placed.map(item => (
                <div
                  key={item.uid}
                  className="absolute -translate-x-1/2 -translate-y-1/2 group"
                  style={{ left: `${item.x}%`, top: `${item.y}%`, width: item.type === 'eq' ? '18%' : '9%' }}
                  data-testid={`placed-${item.uid}`}
                >
                  <img src={`${API}${item.img}`} alt={item.name} className="w-full h-auto drop-shadow-2xl select-none pointer-events-none" />
                  <button
                    onClick={(e) => { e.stopPropagation(); removePlaced(item.uid); }}
                    className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 shadow"
                    data-testid={`remove-${item.uid}`}
                  ><Trash2 className="w-3 h-3" /></button>
                </div>
              ))}

              {/* Carrying preview cursor */}
              {carrying && mode === 'empty' && (
                <div className="absolute top-2 left-2 bg-black/70 text-white text-xs px-2 py-1 rounded flex items-center gap-1 pointer-events-none">
                  <img src={`${API}${carrying.img}`} alt="" className="w-6 h-6 object-contain" />
                  Click canvas to place <b>{carrying.name}</b>
                </div>
              )}
            </div>

            {/* Canvas footer */}
            <div className="flex flex-wrap items-center justify-between gap-2 mt-3">
              <div className="text-xs text-slate-400 flex items-center gap-2">
                <Info className="w-3.5 h-3.5" />
                {mode === 'loaded'
                  ? 'Hover any item and click to operate it (tap, flush, light, fan …).'
                  : `Placed: ${placed.length} item(s). Pick from the left palette then click the scene.`}
              </div>
              {mode === 'empty' && placed.length > 0 && (
                <Button size="sm" variant="outline" onClick={clearCanvas} className="border-white/20 text-white hover:bg-white/10" data-testid="clear-canvas">
                  <Trash2 className="w-3.5 h-3.5 mr-1" /> Clear canvas
                </Button>
              )}
            </div>
          </main>
        </div>

        <div className="text-center mt-8 text-xs text-slate-500">
          Photo-real backdrops and equipment rendered by Gemini Nano Banana. Cartoon tools drawn in caricature style. Click "Regenerate" anytime.
        </div>
      </div>
    </div>
  );
}

// -----------------------------------------------------------------------------
// Palette panel (equipment or tools)
// -----------------------------------------------------------------------------
function PalettePanel({ title, icon, hint, items, kind, carrying, setCarrying, regenSingle }) {
  return (
    <div className="rounded-xl border border-white/10 bg-white/5 p-3" data-testid={`palette-${kind}`}>
      <div className="flex items-center gap-2 mb-1">
        {icon}
        <div className="text-sm font-bold">{title}</div>
        <div className="text-[10px] text-slate-500 ml-auto">{items.length}</div>
      </div>
      <div className="text-[11px] text-slate-400 mb-2">{hint}</div>
      <div className="grid grid-cols-3 gap-2">
        {items.map(it => {
          const picked = carrying && carrying.id === it.id && carrying.type === kind;
          const canPick = !!setCarrying && it.ready;
          return (
            <div key={it.id} className="relative group">
              <button
                onClick={() => canPick && setCarrying({ type: kind, id: it.id, name: it.name, img: it.img })}
                disabled={!canPick}
                title={it.name}
                className={`aspect-square w-full rounded-lg border-2 bg-white/5 overflow-hidden transition-all ${picked ? 'border-amber-400 shadow-[0_0_20px_2px_rgba(253,224,71,0.4)]' : 'border-white/10 hover:border-white/30'} ${canPick ? '' : 'cursor-default'}`}
                data-testid={`palette-item-${kind}-${it.id}`}
              >
                {it.ready ? (
                  <img src={`${API}${it.img}`} alt={it.name} className="w-full h-full object-contain bg-white" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-[10px] text-slate-500">not rendered</div>
                )}
              </button>
              <div className="text-[10px] text-slate-300 mt-1 truncate text-center">{it.name}</div>
              <button
                onClick={() => regenSingle(kind, it.id)}
                className="absolute top-1 right-1 opacity-0 group-hover:opacity-100 bg-black/70 text-white p-1 rounded"
                title="Regenerate this asset"
                data-testid={`palette-regen-${kind}-${it.id}`}
              ><RefreshCw className="w-3 h-3" /></button>
            </div>
          );
        })}
      </div>
    </div>
  );
}
